/**
 * SensiMessage - Individual message component for chat
 * Renders user and assistant messages with formatting and citations
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Icons } from '../../lib/Icons';
import SensiActivityCard from './SensiActivityCard';

// Simple markdown-like formatting
const formatContent = (content) => {
  if (!content) return null;

  // Split into paragraphs
  const paragraphs = content.split('\n\n');

  return paragraphs.map((paragraph, pIndex) => {
    // Handle bullet points
    if (paragraph.includes('\n•') || paragraph.startsWith('•')) {
      const lines = paragraph.split('\n');
      return (
        <ul key={pIndex} className="list-none space-y-1 my-2">
          {lines.map((line, lIndex) => {
            if (line.startsWith('•')) {
              return (
                <li key={lIndex} className="flex items-start gap-2">
                  <span className="text-violet-400 mt-1.5">•</span>
                  <span>{formatInline(line.substring(1).trim())}</span>
                </li>
              );
            }
            return <span key={lIndex}>{formatInline(line)}</span>;
          })}
        </ul>
      );
    }

    // Handle numbered lists
    if (/^\d+\./.test(paragraph)) {
      const lines = paragraph.split('\n');
      return (
        <ol key={pIndex} className="list-none space-y-1 my-2">
          {lines.map((line, lIndex) => {
            const match = line.match(/^(\d+)\.\s*(.*)/);
            if (match) {
              return (
                <li key={lIndex} className="flex items-start gap-2">
                  <span className="text-violet-400 font-medium min-w-[1.5rem]">{match[1]}.</span>
                  <span>{formatInline(match[2])}</span>
                </li>
              );
            }
            return <span key={lIndex}>{formatInline(line)}</span>;
          })}
        </ol>
      );
    }

    // Regular paragraph
    return (
      <p key={pIndex} className="my-2">
        {formatInline(paragraph)}
      </p>
    );
  });
};

// Format inline elements (bold, italic)
const formatInline = (text) => {
  if (!text) return null;

  // Replace **text** with bold
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={index} className="font-semibold text-white">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return part;
  });
};

const SensiMessage = ({ message }) => {
  const isUser = message.role === 'user';
  const isError = message.isError;

  return (
    <div className={`flex items-start gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      {/* Avatar */}
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
        isUser 
          ? 'bg-gradient-to-br from-blue-500 to-cyan-600' 
          : isError 
            ? 'bg-gradient-to-br from-red-500 to-orange-600'
            : 'bg-gradient-to-br from-violet-500 to-purple-600'
      }`}>
        {isUser ? (
          <Icons.User className="w-4 h-4 text-white" />
        ) : (
          <Icons.Sparkles className="w-4 h-4 text-white" />
        )}
      </div>

      {/* Message content */}
      <div className={`max-w-[85%] ${isUser ? 'text-right' : ''}`}>
        <div className={`rounded-2xl px-4 py-3 ${
          isUser 
            ? 'bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-tr-sm border border-blue-500/20' 
            : isError
              ? 'bg-red-500/10 rounded-tl-sm border border-red-500/20'
              : 'bg-slate-800 rounded-tl-sm border border-slate-700/50'
        }`}>
          <div className={`text-sm leading-relaxed ${
            isUser ? 'text-white' : isError ? 'text-red-300' : 'text-slate-200'
          }`}>
            {formatContent(message.content)}
          </div>
        </div>

        {/* Citations */}
        {message.citations && message.citations.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {message.citations.map((citation, index) => (
              <span
                key={index}
                className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-slate-800/50 text-slate-400 border border-slate-700/50"
                title={citation.source || 'Internal guidance'}
              >
                <Icons.BookOpen className="w-3 h-3" />
                <span className="truncate max-w-[150px]">{citation.title}</span>
              </span>
            ))}
          </div>
        )}

        {/* Suggested Activities */}
        {message.suggestedActivities && message.suggestedActivities.length > 0 && (
          <div className="mt-3 space-y-2">
            <p className="text-xs text-slate-500 flex items-center gap-1">
              <Icons.Puzzle className="w-3 h-3" />
              Related activities:
            </p>
            <div className="grid gap-2">
              {message.suggestedActivities.map((activity, index) => (
                <SensiActivityCard key={index} activity={activity} />
              ))}
            </div>
          </div>
        )}

        {/* Suggested Articles */}
        {message.suggestedArticles && message.suggestedArticles.length > 0 && (
          <div className="mt-3 space-y-2">
            <p className="text-xs text-slate-500 flex items-center gap-1">
              <Icons.BookOpen className="w-3 h-3" />
              Read more:
            </p>
            <div className="grid gap-2">
              {message.suggestedArticles.map((article, index) => (
                <Link
                  key={index}
                  to={`/solo/articles/${article.slug}`}
                  className="block p-3 bg-gradient-to-r from-violet-500/10 to-purple-500/10 rounded-xl border border-violet-500/20 hover:border-violet-500/40 transition-all group"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                      <Icons.Document className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-white group-hover:text-violet-300 transition-colors line-clamp-1">
                        {article.title}
                      </h4>
                      {article.summary && (
                        <p className="text-xs text-slate-400 mt-0.5 line-clamp-2">
                          {article.summary}
                        </p>
                      )}
                      {article.category && (
                        <span className="inline-block mt-1 px-2 py-0.5 bg-violet-500/20 text-violet-300 rounded-full text-xs capitalize">
                          {article.category.replace('-', ' ')}
                        </span>
                      )}
                    </div>
                    <Icons.ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-violet-400 transition-colors flex-shrink-0" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Timestamp */}
        {message.timestamp && (
          <p className={`text-xs text-slate-500 mt-1 ${isUser ? 'text-right' : ''}`}>
            {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </p>
        )}
      </div>
    </div>
  );
};

export default SensiMessage;



